/**
 * Login — Authentication page
 *
 * Supports email/password and Google SSO.
 * Uses AuthContext for all auth operations and i18n for labels.
 */

import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import KDInput from "components/KDInput";
import KDAlert from "components/KDAlert";

import { AuthContext } from "context/AuthContext";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import logger from "services/logger";

function Login() {
  const { login, loginWithGoogle, currentUser, activeRole, authLoading } = useContext(AuthContext);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || null;

  // Redirect if already authenticated
  useEffect(() => {
    if (currentUser && activeRole && !authLoading) {
      const target = from || `/${activeRole}/dashboard`;
      navigate(target, { replace: true });
    }
  }, [currentUser, activeRole, authLoading, from, navigate]);

  /**
   * Map Firebase error codes to user-friendly i18n keys.
   */
  const getErrorMessage = (err) => {
    const code = err?.code || "";
    switch (code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return t("error.invalidCredentials");
      case "auth/user-disabled":
        return t("error.accountDisabled");
      case "auth/too-many-requests":
        return t("error.tooManyRequests");
      case "auth/network-request-failed":
        return t("error.networkError");
      case "auth/popup-closed-by-user":
        return null; // User cancelled — no error shown
      default:
        return err.message || t("error.loginFailed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // onAuthStateChanged will handle state + redirect
    } catch (err) {
      logger.error("[Login]", "Login failed", err);
      const msg = getErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      // onAuthStateChanged will handle state + redirect
    } catch (err) {
      logger.error("[Login]", "Google login failed", err);
      const msg = getErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KDBox
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: darkMode
          ? "linear-gradient(195deg, #0F1114 0%, #161B1F 100%)"
          : "linear-gradient(195deg, #F8FAFC 0%, #F0FDFA 100%)",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: 3,
          boxShadow: darkMode ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header */}
        <KDBox textAlign="center" mb={3}>
          <KDTypography variant="h4" fontWeight="bold">
            {t("auth.loginTitle")}
          </KDTypography>
          <KDTypography variant="body2" color="text" mt={1}>
            {t("auth.loginSubtitle")}
          </KDTypography>
        </KDBox>

        {/* Error */}
        {error && (
          <KDAlert color="error" sx={{ mb: 2 }}>
            {error}
          </KDAlert>
        )}

        {/* Google SSO */}
        <KDButton
          variant="outlined"
          color="dark"
          fullWidth
          onClick={handleGoogle}
          disabled={loading}
          sx={{ mb: 2, textTransform: "none", fontWeight: 500 }}
        >
          <Box component="span" sx={{ mr: 1, display: "inline-flex", alignItems: "center" }}>
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.53l7.97-5.94z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.94C6.51 42.62 14.62 48 24 48z"/>
            </svg>
          </Box>
          {t("auth.continueWithGoogle")}
        </KDButton>

        <Divider sx={{ my: 2 }}>
          <KDTypography variant="caption" color="text" sx={{ px: 2 }}>
            {t("common.or")}
          </KDTypography>
        </Divider>

        {/* Email / Password form */}
        <KDBox component="form" onSubmit={handleSubmit}>
          <KDBox mb={2}>
            <KDInput
              label={t("auth.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </KDBox>
          <KDBox mb={2}>
            <KDInput
              label={t("auth.password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </KDBox>

          <KDButton
            type="submit"
            variant="gradient"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ mt: 1 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : t("auth.login")}
          </KDButton>
        </KDBox>

        {/* Links */}
        <KDBox mt={3} textAlign="center">
          <KDTypography variant="caption" color="text">
            <Link to="/reset-password" style={{ color: "inherit" }}>
              {t("auth.forgotPassword")}
            </Link>
          </KDTypography>
        </KDBox>
        <KDBox mt={1} textAlign="center">
          <KDTypography variant="caption" color="text">
            {t("auth.noAccount")}{" "}
            <Link to="/authentication/signup" style={{ color: "#0D9488", fontWeight: 600 }}>
              {t("auth.signUp")}
            </Link>
          </KDTypography>
        </KDBox>
      </Card>
    </KDBox>
  );
}

export default Login;
